import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Save, Edit2, Car, Bike, Users, Wrench, Send } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const ROUNDS = ['r1', 'r2', 'r3', 'r4', 'r5'];
const ROUND_LABELS = ['R1', 'R2', 'R3', 'R4', 'R5'];

export default function ManualPointsEntry() {
  const [activeTab, setActiveTab] = useState('drivers');
  const [entities, setEntities] = useState({
    drivers: [], riders: [], cars: [], bikes: [], tuners: [], motoTuners: []
  });
  const [pointsData, setPointsData] = useState({});
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [publishModal, setPublishModal] = useState({ open: false, league: '' });
  const [publishRound, setPublishRound] = useState('');

  useEffect(() => {
    fetchEntities();
    fetchCurrentPoints();
  }, []);

  const fetchEntities = async () => {
    try {
      const [driversRes, ridersRes, carsRes, bikesRes, tunersRes, motoTunersRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/drivers`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/moto/riders`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/cars`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/moto/bikes`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/tuners`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/moto/tuners`, { withCredentials: true })
      ]);
      setEntities({
        drivers: driversRes.data, riders: ridersRes.data,
        cars: carsRes.data, bikes: bikesRes.data,
        tuners: tunersRes.data, motoTuners: motoTunersRes.data
      });
    } catch (error) {
      console.error('Failed to fetch entities:', error);
    }
  };

  const fetchCurrentPoints = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/points/all`, { withCredentials: true });
      const map = {};

      const processResults = (results, prefix, idField) => {
        results?.forEach(r => {
          const key = `${prefix}_${r[idField]}`;
          const rp = r.round_points || {};
          map[key] = {
            r1: rp.r1 || 0, r2: rp.r2 || 0, r3: rp.r3 || 0,
            r4: rp.r4 || 0, r5: rp.r5 || 0,
            total: r.total_points || 0,
            saved: true
          };
        });
      };

      processResults(response.data.driver_results, 'driver', 'driver_id');
      processResults(response.data.rider_results, 'rider', 'rider_id');
      processResults(response.data.car_results, 'car', 'car_id');
      processResults(response.data.bike_results, 'bike', 'bike_id');
      processResults(response.data.tuner_results, 'tuner', 'tuner_id');
      processResults(response.data.moto_tuner_results, 'moto_tuner', 'tuner_id');

      setPointsData(map);
    } catch (error) {
      console.error('Failed to fetch current points:', error);
    }
  };

  const getPointsForEntity = (entityType, entityId) => {
    const key = `${entityType}_${entityId}`;
    return pointsData[key] || { r1: 0, r2: 0, r3: 0, r4: 0, r5: 0, total: 0 };
  };

  const handleRoundChange = (entityType, entityId, round, value) => {
    const key = `${entityType}_${entityId}`;
    const numVal = value === '' ? 0 : parseInt(value) || 0;

    setPointsData(prev => {
      const current = prev[key] || { r1: 0, r2: 0, r3: 0, r4: 0, r5: 0, total: 0 };
      const updated = { ...current, [round]: numVal, saved: false };
      updated.total = ROUNDS.reduce((sum, r) => sum + (updated[r] || 0), 0);
      return { ...prev, [key]: updated };
    });
  };

  const saveAllPoints = async () => {
    setSaving(true);
    const entries = [];

    Object.keys(pointsData).forEach(key => {
      const data = pointsData[key];
      if (data.saved) return; // Skip unchanged

      const parts = key.split('_');
      const entityType = parts[0] === 'moto' ? 'moto_tuner' : parts[0];
      const entityId = parts[0] === 'moto' ? parts.slice(2).join('_') : parts.slice(1).join('_');

      entries.push({
        entity_type: entityType,
        entity_id: entityId,
        points: data.total,
        round_points: { r1: data.r1 || 0, r2: data.r2 || 0, r3: data.r3 || 0, r4: data.r4 || 0, r5: data.r5 || 0 }
      });
    });

    if (entries.length === 0) {
      toast.info('No changes to save');
      setSaving(false);
      return;
    }

    try {
      const response = await axios.post(`${BACKEND_URL}/api/admin/points/manual/bulk`, { entries }, { withCredentials: true });
      toast.success(`Saved ${response.data.updated} point entries`);
      fetchCurrentPoints();
    } catch (error) {
      toast.error('Failed to save points');
    } finally {
      setSaving(false);
    }
  };

  const publishResults = async (league) => {
    setPublishModal({ open: true, league });
    setPublishRound('');
  };

  const confirmPublish = async () => {
    const roundNumber = parseInt(publishRound);
    if (!roundNumber || roundNumber < 1 || roundNumber > 10) {
      toast.error('Enter a valid round number (1-10)');
      return;
    }
    const { league } = publishModal;
    setPublishModal({ open: false, league: '' });
    setPublishing(true);
    try {
      await saveAllPoints();
      await axios.post(`${BACKEND_URL}/api/admin/results/publish`, {
        round_number: roundNumber,
        league
      }, { withCredentials: true });
      toast.success(`Round ${roundNumber} results published! Players have been notified.`);
    } catch (error) {
      toast.error('Failed to publish results');
    } finally {
      setPublishing(false);
    }
  };

  const renderEntityTable = (entityList, entityType, idField, nameField) => {
    return (
      <div className="overflow-x-auto" data-testid={`points-table-${entityType}`}>
        <table className="w-full">
          <thead>
            <tr className="border-b border-zinc-700">
              <th className="text-left p-3 text-zinc-400 text-sm sticky left-0 bg-card z-10">Name</th>
              <th className="text-left p-3 text-zinc-400 text-sm">Team</th>
              {ROUND_LABELS.map((label) => (
                <th key={label} className="text-center p-2 text-zinc-400 text-sm w-20 font-mono">{label}</th>
              ))}
              <th className="text-center p-2 text-yellow-500 text-sm w-20 font-mono font-bold">TOTAL</th>
            </tr>
          </thead>
          <tbody>
            {entityList.map(entity => {
              const pts = getPointsForEntity(entityType, entity[idField]);
              const isChanged = pts.saved === false;

              return (
                <tr
                  key={entity[idField]}
                  className={`border-b border-zinc-800 ${isChanged ? 'bg-yellow-900/10' : ''}`}
                  data-testid={`points-row-${entityType}-${entity[idField]}`}
                >
                  <td className="p-3 font-medium text-sm sticky left-0 bg-card z-10 whitespace-nowrap">{entity[nameField]}</td>
                  <td className="p-3 text-zinc-400 text-sm whitespace-nowrap">{entity.tuner || entity.team || '-'}</td>
                  {ROUNDS.map((round) => (
                    <td key={round} className="p-1">
                      <input
                        type="number"
                        value={pts[round] || ''}
                        onChange={(e) => handleRoundChange(entityType, entity[idField], round, e.target.value)}
                        className="w-full bg-zinc-900 border border-zinc-700 px-2 py-2 text-center text-white font-mono text-sm focus:border-primary outline-none"
                        placeholder="0"
                        data-testid={`points-${entityType}-${entity[idField]}-${round}`}
                      />
                    </td>
                  ))}
                  <td className="p-1">
                    <div className="w-full bg-zinc-800 border border-zinc-600 px-2 py-2 text-center text-yellow-400 font-mono text-sm font-bold"
                         data-testid={`points-${entityType}-${entity[idField]}-total`}>
                      {pts.total}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  const tabs = [
    { id: 'drivers', label: 'Drivers', icon: Users, color: 'text-primary' },
    { id: 'cars', label: 'Cars', icon: Car, color: 'text-primary' },
    { id: 'tuners', label: 'Tuners', icon: Wrench, color: 'text-primary' },
    { id: 'riders', label: 'Riders', icon: Users, color: 'text-secondary' },
    { id: 'bikes', label: 'Bikes', icon: Bike, color: 'text-secondary' },
    { id: 'motoTuners', label: 'Moto Tuners', icon: Wrench, color: 'text-secondary' }
  ];

  return (
    <div className="space-y-6" data-testid="manual-points-entry">
      <div className="bg-card border border-zinc-800 p-6">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className="font-heading font-bold text-2xl flex items-center">
              <Edit2 className="mr-2 text-yellow-500" size={24} />
              Manual Points Entry
            </h2>
            <p className="text-zinc-400 text-sm mt-1">
              Enter points per round for all entities. Total is automatically calculated.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={saveAllPoints}
              disabled={saving}
              className="flex items-center space-x-2 bg-zinc-700 hover:bg-zinc-600 text-white px-5 py-2 font-bold transition-colors disabled:opacity-50"
              data-testid="save-all-points-btn"
            >
              <Save size={18} />
              <span>{saving ? 'Saving...' : 'Save Draft'}</span>
            </button>
            <div className="relative group">
              <button
                disabled={publishing}
                className="flex items-center space-x-2 bg-primary hover:bg-primary/80 text-white px-5 py-2 font-bold transition-colors disabled:opacity-50"
                data-testid="publish-results-btn"
                onClick={() => document.getElementById('publish-menu').classList.toggle('hidden')}
              >
                <Send size={18} />
                <span>{publishing ? 'Publishing...' : 'Publish Results'}</span>
              </button>
              <div id="publish-menu" className="hidden absolute right-0 top-full mt-1 bg-zinc-800 border border-zinc-700 shadow-xl z-50">
                <button
                  onClick={() => { document.getElementById('publish-menu').classList.add('hidden'); publishResults('racing'); }}
                  className="block w-full text-left px-4 py-2 text-sm hover:bg-zinc-700 text-primary font-medium whitespace-nowrap"
                  data-testid="publish-racing-btn"
                >
                  Publish Superturismo
                </button>
                <button
                  onClick={() => { document.getElementById('publish-menu').classList.add('hidden'); publishResults('moto'); }}
                  className="block w-full text-left px-4 py-2 text-sm hover:bg-zinc-700 text-secondary font-medium whitespace-nowrap"
                  data-testid="publish-moto-btn"
                >
                  Publish MotoMSF
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Entity type tabs */}
        <div className="flex flex-wrap gap-2 mb-6">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              data-testid={`points-tab-${tab.id}`}
              className={`flex items-center space-x-2 px-4 py-2 font-bold transition-colors ${
                activeTab === tab.id
                  ? tab.id.includes('rider') || tab.id.includes('bike') || tab.id.includes('moto')
                    ? 'bg-secondary text-black'
                    : 'bg-primary text-white'
                  : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
              }`}
            >
              <tab.icon size={16} />
              <span>{tab.label}</span>
              <span className="text-xs opacity-70">({entities[tab.id]?.length || 0})</span>
            </button>
          ))}
        </div>

        {/* Content */}
        {activeTab === 'drivers' && renderEntityTable(entities.drivers, 'driver', 'driver_id', 'name')}
        {activeTab === 'riders' && renderEntityTable(entities.riders, 'rider', 'rider_id', 'name')}
        {activeTab === 'cars' && renderEntityTable(entities.cars, 'car', 'car_id', 'name')}
        {activeTab === 'bikes' && renderEntityTable(entities.bikes, 'bike', 'bike_id', 'name')}
        {activeTab === 'tuners' && renderEntityTable(entities.tuners, 'tuner', 'tuner_id', 'name')}
        {activeTab === 'motoTuners' && renderEntityTable(entities.motoTuners, 'moto_tuner', 'tuner_id', 'name')}
      </div>

      {publishModal.open && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" data-testid="publish-modal">
          <div className="bg-zinc-900 border border-zinc-700 p-6 w-full max-w-sm">
            <h3 className="font-heading font-bold text-lg mb-1">
              Publish {publishModal.league === 'racing' ? 'Superturismo' : 'MotoMSF'} Results
            </h3>
            <p className="text-zinc-400 text-sm mb-4">This will save all changes and notify all players.</p>
            <label className="block text-sm text-zinc-400 mb-1">Round Number</label>
            <input
              data-testid="publish-round-input"
              type="number"
              min="1"
              max="10"
              value={publishRound}
              onChange={(e) => setPublishRound(e.target.value)}
              placeholder="e.g. 1"
              className="w-full bg-zinc-800 border border-zinc-600 px-4 py-2 text-white mb-4 focus:border-primary focus:outline-none"
              autoFocus
            />
            <div className="flex gap-3">
              <button
                onClick={() => setPublishModal({ open: false, league: '' })}
                className="flex-1 bg-zinc-700 hover:bg-zinc-600 text-white py-2 font-bold transition-colors"
              >
                Cancel
              </button>
              <button
                data-testid="confirm-publish-btn"
                onClick={confirmPublish}
                disabled={!publishRound}
                className="flex-1 bg-primary hover:bg-primary/80 text-white py-2 font-bold transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                <Send size={16} />
                Publish
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
